import React, { useMemo } from 'react';
import './Dashboard.css';

const Dashboard = ({ reviews, onNavigateToReviews }) => {
  // Calculate overview statistics
  const statistics = useMemo(() => {
    if (!reviews || reviews.length === 0) {
      return {
        totalReviews: 0,
        uniqueDocuments: 0,
        uniqueReviewers: 0,
        statusBreakdown: {},
        recentReviews: [],
        topReviewers: []
      };
    }

    // Basic counts
    const totalReviews = reviews.length;
    const uniqueDocuments = new Set(reviews.map(r => r.document_id)).size;
    const uniqueReviewers = new Set(reviews.map(r => r.reviewer_id)).size;

    // Status breakdown
    const statusBreakdown = reviews.reduce((acc, review) => {
      const status = review.current_status || review.new_status;
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});


    // Recent reviews (last 10)
    const sortedByDate = [...reviews].sort((a, b) => new Date(b.review_date) - new Date(a.review_date));
    const recentReviews = sortedByDate.slice(0, 10);

    // Top reviewers by review count
    const reviewerCounts = reviews.reduce((acc, review) => {
      acc[review.reviewer_id] = (acc[review.reviewer_id] || 0) + 1;
      return acc;
    }, {});

    const topReviewers = Object.entries(reviewerCounts)
      .map(([reviewer, count]) => ({ reviewer, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      totalReviews,
      uniqueDocuments,
      uniqueReviewers,
      statusBreakdown,
      recentReviews,
      topReviewers
    };
  }, [reviews]);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'approved': return '#28a745';
      case 'processing': return '#ffc107';
      case 'failed': return '#dc3545';
      case 'rejected': return '#dc3545';
      case 'review_required': return '#fd7e14';
      default: return '#6c757d';
    }
  };

  if (!reviews || reviews.length === 0) {
    return (
      <div className="dashboard">
        <h2>Dashboard</h2>
        <div className="no-data">
          <p>No review data available. Load some document reviews to see dashboard statistics.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <h2>Overview</h2>

      {/* Key Statistics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-value">{statistics.totalReviews}</div>
            <div className="stat-label">Total Reviews</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📄</div>
          <div className="stat-content">
            <div className="stat-value">{statistics.uniqueDocuments}</div>
            <div className="stat-label">Unique Documents</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <div className="stat-value">{statistics.uniqueReviewers}</div>
            <div className="stat-label">Active Reviewers</div>
          </div>
        </div>

      </div>

      {/* Status Breakdown */}
      <div className="dashboard-section">
        <h3>Review Status Breakdown</h3>
        <div className="status-breakdown">
          {Object.entries(statistics.statusBreakdown).map(([status, count]) => (
            <div 
              key={status} 
              className="status-item clickable"
              onClick={() => onNavigateToReviews({ currentStatus: status })}
              title={`Click to view all ${status} reviews`}
            >
              <div
                className="status-bar"
                style={{
                  width: `${(count / statistics.totalReviews) * 100}%`,
                  backgroundColor: getStatusColor(status)
                }}
              ></div>
              <div className="status-info">
                <span className="status-name">{status}</span>
                <span className="status-count">{count} ({Math.round((count / statistics.totalReviews) * 100)}%)</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="dashboard-columns">
        {/* Top Reviewers */}
        <div className="dashboard-section">
          <h3>Top Reviewers</h3>
          <div className="top-reviewers">
            {statistics.topReviewers.map((reviewer, index) => (
              <div 
                key={reviewer.reviewer} 
                className="reviewer-item clickable"
                onClick={() => onNavigateToReviews({ reviewer: reviewer.reviewer })}
                title={`Click to view all reviews by ${reviewer.reviewer}`}
              >
                <div className="reviewer-rank">#{index + 1}</div>
                <div className="reviewer-info">
                  <div className="reviewer-name">{reviewer.reviewer}</div>
                  <div className="reviewer-count">{reviewer.count} reviews</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Reviews */}
        <div className="dashboard-section">
          <h3>Recent Reviews</h3>
          <div className="recent-reviews">
            {statistics.recentReviews.map((review) => (
              <div 
                key={review.review_id} 
                className="recent-review-item clickable"
                onClick={() => onNavigateToReviews({ documentId: review.document_id.toString() })}
                title={`Click to view document #${review.document_id} reviews`}
              >
                <div className="review-header">
                  <span className="review-id">Review #{review.review_id}</span>
                  <span className="review-date">{formatDate(review.review_date)}</span>
                </div>
                <div className="review-details">
                  <span>Doc #{review.document_id}</span>
                  <span>by {review.reviewer_id}</span>
                  <span
                    className="review-status"
                    style={{ color: getStatusColor(review.current_status || review.new_status) }}
                  >
                    {review.current_status || review.new_status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;